import { Directionality } from '@angular/cdk/bidi';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppStateService } from '@pcplus/core';
import {
  AcknowledgeDialogComponent,
  AcknowledgeDialogSettings
} from '@pcplus/infrastructure';
import { PermissionsService, Permission, EmptyGuid } from '@pcplus/security';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { InvitationRequest } from '../../../dto/invitation-request';
import { ProtectedResource } from '../../../dto/protected-resource';
import {
  ProtectedResources,
  SafeMFASystem
} from '../../../dto/security-identifier';
import { UpdateProtectedResource } from '../../../dto/update-protected-resource';
import { MfaService } from '../../../services/mfa.service';
import { AddUsersComponent } from '../add-users/add-users.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatFabButton, MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCard, MatCardHeader, MatCardSubtitle, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';

@Component({
    selector: 'mfa-protected-resource',
    templateUrl: './protected-resource.component.html',
    styleUrls: ['./protected-resource.component.scss'],
    imports: [NgIf, MatFabButton, MatTooltip, MatIcon, FlexModule, MatCard, MatCardHeader, MatIconButton, RouterLink, MatCardSubtitle, MatCardTitle, MatFormField, MatInput, FormsModule, MatCardContent, MatButton, CdkCopyToClipboard, MatCheckbox, MatDivider, MatCardActions, AsyncPipe]
})
export class ProtectedResourceComponent implements OnInit, OnDestroy {
  constructor(
    public mfa: MfaService,
    private route: ActivatedRoute,
    private router: Router,
    public permissions: PermissionsService,
    private stateService: AppStateService,
    private dir: Directionality,
    private dialog: MatDialog,
    public snackBar: MatSnackBar
  ) {}

  onDestroy$ = new Subject<void>();
  permission = Permission;
  activity = [SafeMFASystem, ProtectedResources];
  customerId: string | null = null;
  resourceId: string | null = null;
  resource: ProtectedResource | null = null;
  downloading = false;
  refresh$ = new Subject<void>();
  editMode = false;
  saving = false;

  ngOnInit(): void {
    this.stateService.updateCurrentModuleName($localize`Protected Resource`);
    combineLatest([
      this.permissions.selectedCustomer,
      this.route.paramMap,
      this.refresh$
    ])
      .pipe(
        filter(
          ([customer, params]) =>
            !!customer && customer !== EmptyGuid && !!params.get('id')
        ),
        tap(([customer]) => (this.customerId = customer)),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([customer, params]) => {
        const id = params.get('id');
        this.resourceId = id;
        this.mfa
          .getProtectedResource(customer!, id!)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((pr) => {
            this.resource = pr;
            if (this.resource) {
              this.stateService.updateCurrentModuleName(
                $localize`Protected Resource` + ' - ' + this.resource.name
              );
            }
          });
      });
    this.refresh$.next();
  }

  downloadProvider() {
    if (
      !this.customerId ||
      !this.resourceId ||
      !this.resource ||
      this.downloading
    ) {
      console.log('returning with nothing');
      return;
    }
    console.log('download in component');
    this.downloading = true;
    this.mfa
      .downloadProvider(this.resourceId, this.customerId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () => (this.downloading = false),
        () => (this.downloading = false)
      );
  }

  edit() {
    if (!this.customerId || !this.resourceId || !this.resource || this.saving) {
      return;
    }

    if (this.editMode) {
      this.saving = true;
      this.mfa
        .updateProtectedResource(
          this.resourceId,
          this.customerId,
          new UpdateProtectedResource(
            this.resource.name,
            this.resource.safeMFAEnabled,
            this.resource.apiServer,
            this.resource.enableLoginCache,
            this.resource.serverTimeout,
            this.resource.allowMicrosoftAccountLogin,
            this.resource.settingsId
          )
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (pr) => {
            this.resource = pr;
            if (this.resource) {
              this.stateService.updateCurrentModuleName(
                $localize`Protected Resource` + ' - ' + this.resource.name
              );
            }
            this.saving = false;
            this.editMode = false;
          },
          () => {
            this.saving = false;
          }
        );
    } else {
      this.editMode = true;
    }
  }

  delete() {
    if (!this.customerId || !this.resourceId || !this.resource) {
      return;
    }

    this.dialog
      .open(AcknowledgeDialogComponent, {
        direction: this.dir.value,
        data: new AcknowledgeDialogSettings(
          $localize`Are you sure?`,
          $localize`This will delete ${this.resource.name}. Are you sure?`
        )
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.mfa
            .deleteProtectedResource(this.customerId!, this.resourceId!)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((r) => {
              if (r) {
                this.router.navigate(['..']).then(
                  () => {},
                  (error) => {
                    if (error) {
                      console.log(error);
                    }
                  }
                );
              } else {
                this.snackBar.open(
                  $localize`Delete failed. Please try again later.`
                );
              }
            });
        }
      });
  }

  addUsers() {
    if (!this.customerId || !this.resourceId) {
      return;
    }
    const request = new InvitationRequest(this.resourceId, false, []);
    request.customerId = this.customerId;
    this.dialog
      .open(AddUsersComponent, {
        data: request,
        direction: this.dir.value
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: InvitationRequest) => {
        if (
          result &&
          this.customerId &&
          result.invitations &&
          result.invitations.length > 0
        ) {
          this.mfa
            .addUsers(this.customerId, result)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
              this.snackBar.open(
                $localize`The users have been added`,
                $localize`OK`,
                {
                  duration: 2000
                }
              );
              this.refresh$.next();
            });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }
}
